import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Database, X } from 'lucide-react';
import { SapVendorMaster } from '@/types/domain/sap-simulator';

interface SapVendorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<SapVendorMaster, 'id' | 'createdAt'>) => Promise<void>;
}

export const SapVendorFormModal: React.FC<SapVendorFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [vendorCode, setVendorCode] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [category] = useState('IT Services & Hardware');
  const [gstNumber, setGstNumber] = useState('07AAAAA9999A1Z1');
  const [contactEmail, setContactEmail] = useState('procurement@vendor.com');
  const [contactPhone] = useState('+91 11 2222 3333');
  const [paymentTerms, setPaymentTerms] = useState('Net 45 Days');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorCode || !vendorName) {
      setError('Vendor Code and Vendor Name are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onSubmit({
        vendorCode: vendorCode.toUpperCase(),
        vendorName,
        category,
        gstNumber,
        contactEmail,
        contactPhone,
        paymentTerms,
        riskRating: 'LOW',
        status: 'ACTIVE',
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create SAP Vendor.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 font-heading relative z-50">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading text-base font-extrabold text-foreground">Create SAP Vendor Master</h3>
              <p className="text-xs text-foreground-secondary font-medium">Add vendor record for PO and Invoice 3-way matching.</p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-xs font-bold text-rose-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-bold text-foreground block mb-1">Vendor Code *</label>
              <input
                type="text"
                value={vendorCode}
                onChange={(e) => setVendorCode(e.target.value)}
                placeholder="e.g. VND-DELL-04"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-mono text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Payment Terms *</label>
              <input
                type="text"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g. Net 45 Days"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
                required
              />
            </div>
          </div>

          <div>
            <label className="font-bold text-foreground block mb-1">Vendor Name *</label>
            <input
              type="text"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              placeholder="e.g. Dell India Commercial Solutions Pvt Ltd"
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-bold text-foreground block mb-1">GST Number</label>
              <input
                type="text"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-mono text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Contact Email</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
            <Button variant="outline" type="button" onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="rounded-xl font-bold bg-primary text-white">
              {isSubmitting ? 'Saving Vendor...' : 'Save SAP Vendor'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
